import { generateClient } from 'aws-amplify/api';
import { organizationLocalStorageItem } from 'services/organization/atom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from 'core/utils/logger';

// Create a client for GraphQL operations
const client = generateClient();

type NextSubFunction = ({ value }: { value: any }) => void;

type ErrorSubFunction = (error: Error | unknown) => void;

const DEFAULT_AUTH_MODE = 'userPool';

export const graphqlService = {
  query: async (
    gqlQuery: string,
    params?: Record<string, unknown>
  ): Promise<any> => {
    // Remove filter param if it's empty
    if (params?.filter && Object.keys(params.filter).length === 0) {
      delete params.filter;
    }

    const organization = organizationLocalStorageItem();
    const additionalHeaders: { [key: string]: string } = {};
    if (organization) {
      additionalHeaders['organization-id'] = organization?.id;
    }

    try {
      // Set organization ID in the request context
      if (organization?.id) {
        // In v6, we can't directly set headers, so we'll use a different approach
        // We'll add the organization ID to the variables
        params = {
          ...params,
          __organizationId: organization.id
        };
      }

      const result = await client.graphql(
        {
          query: gqlQuery,
          variables: params || {},
          authMode: DEFAULT_AUTH_MODE
        },
        additionalHeaders
      );

      // Handle both GraphQLResult and GraphqlSubscriptionResult types
      return 'data' in result ? result.data : result;
    } catch (error) {
      logger.error(error, { type: 'graphql_query_error' });
      throw error;
    }
  },

  mutation: async (
    gqlQuery: string,
    input?: Record<string, unknown>
  ): Promise<any> => {
    if (input) {
      // Remove empty fields
      Object.keys(input).forEach((key) => {
        if (input[key] === '') {
          delete input[key];
        }
      });
    }

    const organization = organizationLocalStorageItem();

    const additionalHeaders: { [key: string]: string } = {};

    if (organization) {
      additionalHeaders['organization-id'] = organization?.id;
    }

    try {
      // Set organization ID in the request context
      const variables: Record<string, unknown> = { input };

      if (organization?.id) {
        // Add the organization ID to the variables
        variables.__organizationId = organization.id;
      }

      const result = await client.graphql(
        {
          query: gqlQuery,
          variables,
          authMode: DEFAULT_AUTH_MODE
        },
        additionalHeaders
      );

      // Handle both GraphQLResult and GraphqlSubscriptionResult types
      return 'data' in result ? result.data : result;
    } catch (error) {
      logger.error(error, { type: 'graphql_mutation_error' });
      throw error;
    }
  },

  subscribe: (
    subQuery: string,
    params: Record<string, unknown>,
    { next, error }: { next?: NextSubFunction; error?: ErrorSubFunction }
  ) => {
    const organization = organizationLocalStorageItem();

    // Set organization ID in the request context
    if (organization?.id) {
      // Add the organization ID to the variables
      params = {
        ...params,
        __organizationId: organization.id
      };
    }

    try {
      // In v6, subscriptions work differently
      // We need to use the client.graphql method and handle the subscription manually
      const observable = client.graphql({
        query: subQuery,
        variables: params,
        authMode: DEFAULT_AUTH_MODE
      });

      // In v6, observable is a Promise for queries/mutations but has subscribe for subscriptions
      if ('subscribe' in observable) {
        return observable.subscribe({
          next: (value) => {
            if (next) {
              next({
                value
              });
            }
          },
          error: (err: Error | unknown) => {
            if (error) {
              error(err);
            }
          }
        });
      } else {
        // Handle the case where observable is a Promise (not a subscription)
        logger.error('Expected a subscription but received a Promise');
        throw new Error('Invalid subscription operation');
      }
    } catch (err) {
      logger.error(err, { type: 'graphql_subscription_error' });
      throw err;
    }
  }
};

// Helper function to get the current auth token
export const getCurrentAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    logger.error(error, { type: 'auth_token_fetch_error' });
    return null;
  }
};
